/**
 * The symbol this browser was last looking at.
 *
 * ## Why per device rather than on the account
 *
 * The watchlist is synced, because a pin is a considered choice and making it
 * three times is the annoyance. This is the opposite: it is wherever you
 * happened to leave the chart. Syncing it would mean opening the phone during
 * a session moved the desktop chart out from under the trader — and AEGIS is
 * positioned to sit beside a trading platform while somebody works, so a symbol
 * changing without an input is not a small bug.
 *
 * ## Why it is only a hint
 *
 * Read through `defaultSymbol`, which discards it unless the feed still carries
 * it. A remembered value survives a redeploy that changed the universe, and
 * restoring one blindly renders a chart that cannot load.
 */
const KEY = 'aegis.chart.symbol';

const storage = (): Storage | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Safari in private mode throws on access rather than returning null.
    return null;
  }
};

export function loadLastSymbol(): string | null {
  try {
    const raw = storage()?.getItem(KEY) ?? null;
    // Bounded because it is echoed into a feed request. A symbol is a handful
    // of characters; anything longer is a corrupt value, not a contract.
    return raw && raw.length <= 32 ? raw : null;
  } catch {
    return null;
  }
}

export function saveLastSymbol(symbol: string): void {
  try {
    storage()?.setItem(KEY, symbol);
  } catch {
    // A browser that refuses to store this still charts fine; it just opens on
    // the default next time. Not worth surfacing.
  }
}
